/**
 * AdminDomainMigrationPage — `/admin/domain-migration`
 *
 * Operator / tenant tooling to point a custom domain at KStreak.
 * Five panels: Advisor · DNS check · TLS cert check · Reachability probe · Checklist.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Globe, Copy, Loader2, ShieldCheck, Link2, Activity, CheckCircle2,
  AlertTriangle, XCircle, ListChecks, Info, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/domain-migration`;

const statusPill = (s) => {
  if (s === 'pass') return <Badge className="bg-[#34C759]/15 text-[#34C759] text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />PASS</Badge>;
  if (s === 'warn') return <Badge className="bg-[#FF9500]/15 text-[#FF9500] text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />WARN</Badge>;
  if (s === 'fail') return <Badge className="bg-[#FF3B30]/15 text-[#FF3B30] text-[10px]"><XCircle className="w-3 h-3 mr-1" />FAIL</Badge>;
  return <Badge className="bg-[#9CA3AF]/15 text-[#6B7280] text-[10px]">—</Badge>;
};

const copy = async (text, label = 'Copied') => {
  try { await navigator.clipboard.writeText(text); toast.success(label); }
  catch { toast.error('Copy failed'); }
};

const AdminDomainMigrationPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domain, setDomain] = useState('');
  const [mode, setMode] = useState('auto');

  const [advisor, setAdvisor] = useState(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);

  const [dnsResult, setDnsResult] = useState(null);
  const [dnsLoading, setDnsLoading] = useState(false);

  const [certResult, setCertResult] = useState(null);
  const [certLoading, setCertLoading] = useState(false);

  const [probeResult, setProbeResult] = useState(null);
  const [probeLoading, setProbeLoading] = useState(false);

  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'hr';

  const runAdvisor = async () => {
    if (!domain.trim()) return toast.error('Enter a domain');
    setAdvisorLoading(true);
    try {
      const r = await axios.get(`${API}/advisor`, { headers, params: { domain, mode } });
      setAdvisor(r.data);
      toast.success('Records generated');
      loadChecklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Advisor failed');
    } finally { setAdvisorLoading(false); }
  };

  const runDnsCheck = async () => {
    if (!domain.trim()) return toast.error('Enter a domain');
    setDnsLoading(true);
    try {
      const r = await axios.post(`${API}/dns-check`, { domain, mode }, { headers });
      setDnsResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'DNS check failed');
    } finally { setDnsLoading(false); }
  };

  const runCertCheck = async () => {
    if (!domain.trim()) return toast.error('Enter a domain');
    setCertLoading(true);
    try {
      const r = await axios.post(`${API}/cert-check`, { domain }, { headers });
      setCertResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cert check failed');
    } finally { setCertLoading(false); }
  };

  const runProbe = async () => {
    if (!domain.trim()) return toast.error('Enter a domain');
    setProbeLoading(true);
    try {
      const r = await axios.post(`${API}/reachability`, { domain, path: '/api/system/version' }, { headers });
      setProbeResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Probe failed');
    } finally { setProbeLoading(false); }
  };

  const loadChecklist = async () => {
    if (!domain.trim()) return;
    setChecklistLoading(true);
    try {
      const r = await axios.get(`${API}/checklist`, { headers, params: { domain } });
      setChecklist(r.data);
    } catch (e) {
      // Silent if no domain
    } finally { setChecklistLoading(false); }
  };

  const toggleChecklistItem = async (itemId, done) => {
    if (!checklist) return;
    const next = checklist.items.map(it => it.id === itemId ? { ...it, done } : it);
    setChecklist({ ...checklist, items: next });
    try {
      const r = await axios.put(`${API}/checklist`, { domain, items: next.map(it => ({ id: it.id, done: it.done })) }, { headers });
      setChecklist(r.data);
    } catch {
      toast.error('Failed to save checklist');
      loadChecklist();  // revert
    }
  };

  useEffect(() => {
    // Auto-load checklist when user has typed a full-looking domain (contains a dot)
    if (domain.includes('.') && domain.length > 4) {
      loadChecklist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card><CardContent className="p-6 text-[#6B7280]">Admin or HR role required.</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1200px]" data-testid="admin-domain-migration-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
          <Globe className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" data-testid="page-title">Domain Migration</h1>
          <p className="text-sm text-[#6B7280]">Point a custom domain at KStreak — generates DNS records, checks propagation, TLS, and reachability.</p>
        </div>
      </div>

      {/* Target domain input */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Target domain</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs text-[#6B7280]">Domain</Label>
              <Input
                value={domain}
                onChange={e => setDomain(e.target.value)}
                placeholder="e.g. km.acme.com"
                data-testid="domain-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280]">Mode</Label>
              <select
                value={mode}
                onChange={e => setMode(e.target.value)}
                className="w-full h-10 px-3 border rounded-md bg-white text-sm"
                data-testid="mode-select"
              >
                <option value="auto">Auto-detect</option>
                <option value="cname">CNAME (subdomain)</option>
                <option value="apex">A record (apex)</option>
              </select>
            </div>
            <Button onClick={runAdvisor} disabled={advisorLoading} className="bg-[#002FA7]" data-testid="advisor-run-btn">
              {advisorLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
              Generate DNS records
            </Button>
          </div>
          <p className="text-[11px] text-[#9CA3AF]">Subdomains like <code>km.acme.com</code> use CNAME. Apex domains like <code>acme.com</code> need A records (or your provider's ALIAS/ANAME).</p>
        </CardContent>
      </Card>

      {/* Advisor output */}
      {advisor && (
        <Card data-testid="advisor-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-[#002FA7]" />
              Recommended DNS records
              <Badge className="bg-[#002FA7]/15 text-[#002FA7] text-[10px] ml-auto">{advisor.mode.toUpperCase()}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs text-[#6B7280]">
              Canonical host: <code className="bg-[#F3F4F6] px-1.5 py-0.5 rounded">{advisor.canonical_host}</code>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-[11px] uppercase text-[#6B7280]">
                  <tr>
                    <th className="p-2 text-left w-20">Type</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Value</th>
                    <th className="p-2 text-left w-16">TTL</th>
                    <th className="p-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {advisor.records.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`advisor-record-${i}`}>
                      <td className="p-2"><Badge className="bg-[#002FA7]/10 text-[#002FA7] text-[10px]">{r.type}</Badge></td>
                      <td className="p-2 font-mono text-xs break-all">{r.name}</td>
                      <td className="p-2 font-mono text-xs break-all">{r.value}</td>
                      <td className="p-2 text-xs text-[#6B7280]">{r.ttl}</td>
                      <td className="p-2">
                        <Button size="sm" variant="outline" onClick={() => copy(r.value)} data-testid={`advisor-copy-${i}`}>
                          <Copy className="w-3 h-3 mr-1" />Copy
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="text-xs text-[#6B7280] list-disc list-inside space-y-1 pt-2">
              {advisor.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Action buttons row */}
      <div className="grid md:grid-cols-3 gap-3">
        <Button onClick={runDnsCheck} disabled={dnsLoading || !domain} variant="outline" data-testid="dns-check-btn">
          {dnsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
          Run DNS check
        </Button>
        <Button onClick={runCertCheck} disabled={certLoading || !domain} variant="outline" data-testid="cert-check-btn">
          {certLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          Run certificate check
        </Button>
        <Button onClick={runProbe} disabled={probeLoading || !domain} variant="outline" data-testid="reachability-btn">
          {probeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Activity className="w-4 h-4 mr-2" />}
          Run reachability probe
        </Button>
      </div>

      {/* DNS check result */}
      {dnsResult && (
        <Card data-testid="dns-result-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-[#002FA7]" />
              DNS check result
              <span className="ml-auto">{statusPill(dnsResult.overall)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dnsResult.checks.map((c, i) => (
              <div key={i} className="border rounded-lg p-3" data-testid={`dns-check-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  {statusPill(c.status)}
                  <span className="font-medium text-sm">{c.name}</span>
                </div>
                <p className="text-xs text-[#4B5563]">{c.message}</p>
                {c.actual?.length > 0 && (
                  <div className="text-[11px] text-[#6B7280] mt-1">
                    Got: <code className="bg-[#F3F4F6] px-1 py-0.5 rounded break-all">{Array.isArray(c.actual) ? c.actual.join(', ') : String(c.actual)}</code>
                  </div>
                )}
              </div>
            ))}
            <p className="text-[10px] text-[#9CA3AF] pt-1">Checked {dnsResult.checked_at}</p>
          </CardContent>
        </Card>
      )}

      {/* Cert check result */}
      {certResult && (
        <Card data-testid="cert-result-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#002FA7]" />
              TLS certificate
              <span className="ml-auto">{statusPill(certResult.status)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-[#4B5563]">{certResult.message}</p>
            {certResult.subject_cn && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#6B7280]">Subject:</span> <code>{certResult.subject_cn}</code></div>
                <div><span className="text-[#6B7280]">Issuer:</span> <code>{certResult.issuer || '—'}</code></div>
                <div><span className="text-[#6B7280]">Expires:</span> <code>{certResult.not_after}</code></div>
                <div><span className="text-[#6B7280]">Days left:</span> <code>{certResult.days_remaining ?? '—'}</code></div>
              </div>
            )}
            {certResult.sans?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-[#002FA7]">SANs ({certResult.sans.length})</summary>
                <div className="mt-1 text-[11px] text-[#6B7280] break-all">{certResult.sans.slice(0, 20).join(', ')}{certResult.sans.length > 20 ? ` +${certResult.sans.length - 20} more` : ''}</div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reachability result */}
      {probeResult && (
        <Card data-testid="probe-result-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#002FA7]" />
              Reachability probe
              <span className="ml-auto">{statusPill(probeResult.status)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <p className="text-[#4B5563]">{probeResult.message}</p>
            <div className="text-xs text-[#6B7280]">
              <div>URL: <code className="break-all">{probeResult.url}</code></div>
              <div>HTTP: <code>{probeResult.http_code ?? '—'}</code> · Latency: <code>{probeResult.latency_ms}ms</code></div>
            </div>
            {probeResult.body_preview && (
              <details className="text-xs">
                <summary className="cursor-pointer text-[#002FA7]">Response preview</summary>
                <pre className="mt-1 text-[11px] bg-[#F9FAFB] p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">{probeResult.body_preview}</pre>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {/* Checklist */}
      {checklist && (
        <Card data-testid="checklist-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-[#002FA7]" />
              Migration checklist
              <Badge className="bg-[#002FA7]/15 text-[#002FA7] text-[10px] ml-auto">{checklist.progress}% complete</Badge>
              <Button size="sm" variant="ghost" onClick={loadChecklist} disabled={checklistLoading}>
                <RefreshCw className={`w-3 h-3 ${checklistLoading ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden mb-4">
              <div className="h-full bg-[#34C759] transition-all" style={{ width: `${checklist.progress}%` }} />
            </div>
            <div className="space-y-2">
              {checklist.items.map((it) => (
                <label key={it.id} className="flex items-start gap-3 p-2 rounded hover:bg-[#F9FAFB] cursor-pointer" data-testid={`checklist-item-${it.id}`}>
                  <Checkbox
                    checked={it.done}
                    onCheckedChange={(v) => toggleChecklistItem(it.id, !!v)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className={`text-sm ${it.done ? 'line-through text-[#9CA3AF]' : 'text-[#111827]'}`}>
                      {it.label}
                    </div>
                    {it.done_at && (
                      <div className="text-[10px] text-[#9CA3AF]">Marked done {new Date(it.done_at).toLocaleString()}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminDomainMigrationPage;
